import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { VoiceIdentification } from '@utils/types/organization/voice-profile.ts';

interface Props {
  identification: VoiceIdentification;
  resolveSpeakerName?: (speakerId: number) => string | undefined;
}

/** Describe what the model suggested for one label, as a suggestion.
 *
 * Deliberately no percentage: similarity is a comparison score, not a
 * probability that the person is correct, and showing "87%" would invite an
 * editor to read it as certainty. The issue is explicit that a suggestion
 * must be presented as a suggestion.
 *
 * `reason` describes the model's output; it is never a human decision —
 * `override_set` is the authoritative indicator of that. */
export const VoiceSuggestionNote = ({
  identification,
  resolveSpeakerName
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const suggestedName =
    identification.suggested_speaker_id != null
      ? resolveSpeakerName?.(identification.suggested_speaker_id)
      : undefined;

  // "manual" means there is no model suggestion to describe at all.
  if (identification.reason === 'manual' && !suggestedName) return null;

  // The suggested person has since been removed from the directory.
  if (identification.reason === 'speaker_removed') {
    return (
      <div
        className="rounded-md px-3 py-2"
        style={{ backgroundColor: getColour('secondLayer') }}
      >
        <Text as="p" size="xs" tone="muted">
          {t('voice-suggestion-speaker-removed')}
        </Text>
      </div>
    );
  }

  if (!suggestedName) {
    // Recognition ran but could not separate this voice from the runner-up,
    // or found too little usable speech. Both stay usable as "unknown".
    return (
      <div
        className="rounded-md px-3 py-2"
        style={{ backgroundColor: getColour('secondLayer') }}
      >
        <Text as="p" size="xs" tone="muted">
          {t('voice-suggestion-none')}
        </Text>
      </div>
    );
  }

  return (
    <div
      className="flex items-start gap-2 rounded-md px-3 py-2"
      style={{ backgroundColor: getColour('secondLayer') }}
    >
      <Sparkles size={14} className="mt-0.5 shrink-0" aria-hidden />
      <div>
        <Text as="p" size="xs">
          {t('voice-suggestion-is', { name: suggestedName })}
        </Text>
        <Text as="p" size="xs" tone="muted">
          {identification.override_set
            ? t('voice-suggestion-overridden')
            : t('voice-suggestion-review-hint')}
        </Text>
      </div>
    </div>
  );
};
